// Plan 08-05 — useMatchData(matchId) hook composition.
//
// Replaces the Plan 04 stub at `src/ui/match/useMatchData.ts` (which is
// re-exported from here via that file's thin proxy — see Plan 04 SUMMARY
// "useMatchData(matchId) stub — Plan 05 fills with real WS subscription").
//
// Wires:
//   - MatchWsClient (this Plan, Task 1) — primary path; STATE_SNAPSHOT frames
//     drive `playerView`.
//   - startPolling / stopPolling (this Plan, Task 1) — degraded path engaged
//     after 3 reconnect failures via the ws client's `onFallback` callback.
//   - submitTurn (this Plan, Task 2) — bound to the matchId so MatchScreen's
//     SubmitTurnButton calls into it without re-passing matchId.
//
// State machine:
//   { status: 'connecting', playerView: null }
//          │ (mount, bearer present)
//          ▼ MatchWsClient.open()
//   { status: 'connecting', playerView: null }
//          │ ws.onopen → ws.onmessage(STATE_SNAPSHOT)
//          ▼ onSnapshot(view)
//   { status: 'ws', playerView: <view> }
//          │ 3 failed reconnects
//          ▼ onFallback() → startPolling()
//   { status: 'polling', playerView: <last-known> }
//          │ 401 / fatal close
//          ▼
//   { status: 'error', error: <Error> }

import { useStore } from '@nanostores/react'
import { useEffect, useMemo, useRef, useState } from 'react'

import type { PlayerView } from '../game/PlayerViewSchema'
import {
  type PollingState,
  startPolling,
  stopPolling,
} from '../net/polling-fallback'
import {
  type ChessStepResponse,
  type ChessSubmissionBody,
  submitChess,
  submitWord,
  type WordStepResponse,
  type WordSubmissionBody,
} from '../net/turn-submit'
import { MatchWsClient, registerActiveWsClient } from '../net/ws-client'
import { $anonBearer, effectiveBearerJwt } from '../stores/anonSession'
import { $authBearer } from '../stores/auth'

export type MatchDataStatus =
  | 'connecting'
  | 'ws'
  | 'polling'
  | 'error'
  | 'terminated'

export interface UseMatchData {
  playerView: PlayerView | null
  status: MatchDataStatus
  error: Error | null
  /**
   * Plan 14-08 (MUI-09) — server-side match status from the WS envelope
   * (NOT the connection status). Terminal wire values are 'completed' |
   * 'abandoned' | 'timeout_resolved' | 'stalemate_resolved'. The true
   * per-player outcome (win/lose/draw) is derived from
   * `PlayerView.winner_slot` via `deriveTerminalOutcome` in MatchScreen —
   * NOT inferred from this status string. Null when no snapshot has arrived.
   */
  matchStatus: string | null
  /** Submit the Phase-A (word) step against this matchId (Idempotency-Key auto-generated). */
  submitWord: (body: WordSubmissionBody) => Promise<WordStepResponse>
  /** Submit the Phase-B (chess) step against this matchId (Idempotency-Key auto-generated). */
  submitChess: (body: ChessSubmissionBody) => Promise<ChessStepResponse>
  /**
   * Plan 3 Task 4 (two-step turn) — apply a step-response `your_state`
   * PlayerView directly into the live state, bypassing the WS round-trip so
   * the UI flips between word/chess steps immediately. Uses the SAME state
   * transition as the `onSnapshot` WS path (status→'ws'); the authoritative
   * TURN_APPLIED / STATE_SNAPSHOT broadcast that arrives later is idempotent.
   */
  applyLocalView: (view: PlayerView) => void
}

interface InternalState {
  playerView: PlayerView | null
  status: MatchDataStatus
  error: Error | null
  matchStatus: string | null
}

export function useMatchData(matchId: string): UseMatchData {
  // ARCA-93: subscribe to BOTH session sources so a mint / rotation / logout
  // re-runs the connect effect. The effective session is the real bearer when
  // authenticated, else a non-expired anon practice token — mirrors
  // effectiveBearerJwt() (used by the getBearer getter below at connect time).
  const realBearer = useStore($authBearer)
  const anonBearer = useStore($anonBearer)
  const sessionJwt = realBearer
    ? realBearer.jwt
    : anonBearer && anonBearer.exp_ms > Date.now()
      ? anonBearer.jwt
      : null
  const [state, setState] = useState<InternalState>({
    playerView: null,
    status: sessionJwt ? 'connecting' : 'error',
    error: sessionJwt ? null : new Error('NO_BEARER'),
    matchStatus: null,
  })
  const wsRef = useRef<MatchWsClient | null>(null)
  const pollingRef = useRef<PollingState | null>(null)

  useEffect(() => {
    // Reset to a deterministic initial state on every (matchId, session) change.
    if (!sessionJwt) {
      setState({
        playerView: null,
        status: 'error',
        error: new Error('NO_BEARER'),
        matchStatus: null,
      })
      return
    }
    setState({
      playerView: null,
      status: 'connecting',
      error: null,
      matchStatus: null,
    })

    // CR-03 (Plan 09-10): pass a getter — NOT a captured jwt string — so
    // MatchWsClient reads the freshest token on every (re)connect (silent-
    // refresh rotations + logout transitions propagate to the next ws
    // upgrade). ARCA-93: effectiveBearerJwt resolves the real bearer first,
    // else the anon practice token, so guest bot matches connect.
    const client = new MatchWsClient(matchId, () => effectiveBearerJwt(), {
      onSnapshot: (view) => {
        setState((s) => ({
          ...s,
          playerView: view,
          status: 'ws',
          error: null,
        }))
      },
      onStatus: (matchStatus) => {
        setState((s) => ({ ...s, matchStatus }))
      },
      onError: (err) => {
        setState((s) => ({ ...s, status: 'error', error: err }))
      },
      onFallback: () => {
        // Switch to polling. The polling state lives in pollingRef so the
        // unmount cleanup can stop it.
        const ps = startPolling(matchId, {
          onUpdate: (view) => {
            setState((s) => ({
              ...s,
              playerView: view,
              status: 'polling',
              error: null,
            }))
          },
          onError: (e) => {
            setState((s) => ({
              ...s,
              status: 'error',
              error: e instanceof Error ? e : new Error(String(e)),
            }))
          },
          // fix(match) #54 — mirror the WS onStatus handler so a terminal
          // status arriving via polling updates matchStatus (which gates the
          // end-of-match overlay).
          onStatus: (matchStatus) => setState((s) => ({ ...s, matchStatus })),
        })
        pollingRef.current = ps
        setState((s) => ({ ...s, status: 'polling' }))
      },
    })
    wsRef.current = client
    // Plan 21-06 — MatchWsClient.open() is now async (awaits fetchWsTicket
    // before constructing the WebSocket). Fire-and-forget here: errors
    // route through the onError callback above, which transitions state to
    // 'error' just as it did pre-21-06. The `void` prefix is explicit
    // discarding of the Promise so lint/type-checkers don't flag the
    // missing await. MUI-08 (Plan 14-07) race-safety: the unmount cleanup
    // below calls `client.close()` which sets the `_closed` flag BEFORE
    // any reconnect-bound state, so an in-flight fetchWsTicket that
    // resolves AFTER unmount hits the new post-await `if (this._closed)
    // return` guard in ws-client.ts and short-circuits before constructing
    // a WebSocket.
    void client.open()
    // Plan 20-07 META-08 — expose this match's active WS client to the
    // module-scoped `sendEmote()` helper. Cleared in the unmount cleanup
    // so a stale client cannot receive an emote-send after the component
    // tears down.
    registerActiveWsClient(client)

    return () => {
      registerActiveWsClient(null)
      try {
        client.close()
      } catch {
        // ignore — already closed/closing
      }
      wsRef.current = null
      if (pollingRef.current) {
        stopPolling(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [matchId, sessionJwt])

  // Bind the turn submitters to the current matchId. Memoized so MatchScreen's
  // useCallback deps stay stable. submitWord/submitChess are the two-step
  // turn submitters (Plan 3); the combined submitTurn shim was removed once
  // MatchScreen migrated to the two-step flow.
  const submitWordBound = useMemo(
    () => (body: WordSubmissionBody) => submitWord(matchId, body),
    [matchId],
  )
  const submitChessBound = useMemo(
    () => (body: ChessSubmissionBody) => submitChess(matchId, body),
    [matchId],
  )

  // Plan 3 Task 4 — apply a step-response your_state directly. Mirrors the
  // onSnapshot transition so the word→chess flip is immediate (no WS wait).
  const applyLocalView = useMemo(
    () => (view: PlayerView) => {
      setState((s) => ({ ...s, playerView: view, status: 'ws', error: null }))
    },
    [],
  )

  return {
    playerView: state.playerView,
    status: state.status,
    error: state.error,
    matchStatus: state.matchStatus,
    submitWord: submitWordBound,
    submitChess: submitChessBound,
    applyLocalView,
  }
}
